import type {
  CreateDocumentResult,
  DocumentTemplate,
  DocumentTemplateListRequest,
  DocumentTemplateListResponse,
  DocumentTemplateScope,
  DocumentWorkbenchSnapshot,
  SaveAsTemplateBasicInfo,
  SaveAsTemplateExtractResult,
  SaveAsTemplatePreviewInfo,
  SaveAsTemplateRequest,
  SaveAsTemplateResult,
  SaveAsTemplateReviewInfo,
  SaveAsTemplateStructureInfo,
  TemplatePreviewResult,
  WorkbenchDocumentListItem,
} from '@/types/documentWorkbench';

const mockDelay = async () => new Promise((resolve) => window.setTimeout(resolve, 80));

const previewSections = [
  {
    title: '一、标题',
    content: '在签收流程中，仅要求乙方确认文件的数量、种类与封面编号，未包含完整性、清晰度及无缺页确认。',
    highlighted: true,
  },
  {
    title: '二、标题',
    content: '若乙方事后主张收到的是白纸、残叶或内容缺失，我方将难以证明已履行完整交付义务。',
  },
  {
    title: '三、标题',
    content: '建议补充交付验收要点，明确文件版本、范围、附件清单与签收责任人。',
  },
];

const templateSeeds: Array<Omit<DocumentTemplate, 'id' | 'scope'>> = [
  {
    title: '项目阶段评审报告',
    type: 'report',
    version: '2.3',
    skillCount: 7,
    previewTitle: '冰箱冷凝器散热效率',
    previewSections,
  },
  {
    title: '产品规格需求书（SOR）',
    type: 'sor',
    version: '2.3',
    skillCount: 7,
    featured: true,
    previewTitle: '冰箱冷凝器散热效率',
    previewSections,
  },
  {
    title: '产品规格需求书（SOR）',
    type: 'sor',
    version: '2.3',
    skillCount: 7,
    previewTitle: '冰箱冷凝器散热效率',
    previewSections,
  },
  {
    title: '跨部门会议纪要',
    type: 'meeting',
    version: '2.3',
    skillCount: 7,
    previewTitle: '冰箱冷凝器散热效率',
    previewSections,
  },
  {
    title: '跨部门会议纪要',
    type: 'meeting',
    version: '2.3',
    skillCount: 7,
    previewTitle: '冰箱冷凝器散热效率',
    previewSections,
  },
  {
    title: '跨部门会议纪要',
    type: 'meeting',
    version: '2.3',
    skillCount: 7,
    previewTitle: '冰箱冷凝器散热效率',
    previewSections,
  },
  {
    title: '跨部门会议纪要',
    type: 'meeting',
    version: '2.3',
    skillCount: 7,
    previewTitle: '冰箱冷凝器散热效率',
    previewSections,
  },
  {
    title: '跨部门会议纪要',
    type: 'meeting',
    version: '2.3',
    skillCount: 7,
    previewTitle: '冰箱冷凝器散热效率',
    previewSections,
  },
  {
    title: '跨部门会议纪要',
    type: 'meeting',
    version: '2.3',
    skillCount: 7,
    previewTitle: '冰箱冷凝器散热效率',
    previewSections,
  },
  {
    title: '跨部门会议纪要',
    type: 'meeting',
    version: '2.3',
    skillCount: 7,
    previewTitle: '冰箱冷凝器散热效率',
    previewSections,
  },
];

const createTemplates = (scope: DocumentTemplateScope): DocumentTemplate[] =>
  templateSeeds.map((template, index) => ({
    ...template,
    id: `${scope}-template-${index + 1}`,
    scope,
    status: index === 1 && scope === 'mine' ? 'draft' : 'published',
    featured: scope === 'mine' ? index === 0 : template.featured,
    updatedAt: '2026-08-03T10:00:00+08:00',
    description: '沉淀当前文档的章节结构、输入要求与通用检查规则。',
    creatorUserId: '1',
    creatorName: '成吉思汗',
    canManage: scope === 'mine',
    canPublish: index === 1 && scope === 'mine',
    canDelete: scope === 'mine',
    sourceFileName:
      template.type === 'sor' || template.type === 'report'
        ? 'template.xlsx'
        : template.type === 'review'
          ? 'template.pdf'
          : 'template.docx',
  }));

const mockTemplateStore: Record<DocumentTemplateScope, DocumentTemplate[]> = {
  available: [],
  mine: createTemplates('mine'),
  recent: createTemplates('recent'),
  enterprise: createTemplates('enterprise').slice(0, 2),
  project: createTemplates('project').slice(0, 2),
};

mockTemplateStore.available = [
  ...mockTemplateStore.mine,
  ...mockTemplateStore.enterprise,
  ...mockTemplateStore.project,
];

export async function getDocumentTemplates(
  request: DocumentTemplateListRequest,
): Promise<DocumentTemplateListResponse> {
  await mockDelay();
  const keyword = request.keyword?.trim().toLowerCase();
  const scope = request.scope === 'recent' ? 'mine' : request.scope;
  const source = mockTemplateStore[scope] || mockTemplateStore.available;
  let templates = source.filter((template) => (
    keyword
      ? template.title.toLowerCase().includes(keyword)
        || (template.description || '').toLowerCase().includes(keyword)
      : true
  ));
  if (request.documentType) {
    templates = templates.filter((t) => t.type === request.documentType);
  }
  if (request.filterProjectId != null && String(request.filterProjectId)) {
    templates = templates.filter((t) => String(t.projectId || '') === String(request.filterProjectId));
  }
  if (request.creatorUserId != null && String(request.creatorUserId)) {
    templates = templates.filter((t) => t.creatorUserId === String(request.creatorUserId));
  }

  return {
    templates,
    tabCounts: {
      available: mockTemplateStore.available.length,
      enterprise: mockTemplateStore.enterprise.length,
      project: mockTemplateStore.project.length,
      mine: mockTemplateStore.mine.length,
    },
    canCreateBlankDocument: scope === 'mine' || scope === 'recent' || scope === 'available',
    canCreateMineTemplate: true,
    canCreateProjectTemplates: true,
    canManageProjectTemplates: request.projectId !== '0',
    canCreateEnterpriseTemplates: false,
    canManageEnterpriseTemplates: false,
  };
}

const mockFileTypes: WorkbenchDocumentListItem['fileType'][] = [
  'pdf',
  'docx',
  'pptx',
  'xlsx',
  'pdf',
  'docx',
  'pptx',
  'xlsx',
  'doc',
  'ppt',
  'xls',
  'html',
];

const mockDocuments: WorkbenchDocumentListItem[] = Array.from({ length: 12 }, (_, index) => {
  const fileType = mockFileTypes[index] || 'pdf';
  const ext = fileType === 'html' ? 'html' : fileType;
  return {
    id: `document-${index + 1}`,
    name: index === 2 ? `设计文档规范设计....${ext}` : `设计文档规范.${ext}`,
    fileType,
    status: index === 4 ? 'editable' : 'locked',
    updatedAt: '2026-09-09T16:24:00+08:00',
  };
});

export async function getDocumentWorkbenchSnapshot(projectId: string): Promise<DocumentWorkbenchSnapshot> {
  await mockDelay();
  void projectId;

  return {
    documents: mockDocuments,
    activeDocument: {
      id: 'document-5',
      title: '冰箱冷凝器散热效率',
      statusLabel: '文件状态：可更新',
      statusDescription: '可在线查看，下载后将锁定在线编辑功能',
    },
    comments: [
      {
        id: 'comment-1',
        excerpt: '车是会组想易器小舞海而用想易器风的开…',
        author: '王晓亮',
        time: '2026-09-09 16:24',
      },
      {
        id: 'comment-2',
        excerpt: '容言过运院电知今年欢行过',
        author: '王晓亮',
        time: '2026-09-09 16:24',
      },
    ],
  };
}

export async function createBlankDocument(projectId: string): Promise<CreateDocumentResult> {
  await mockDelay();
  return {
    documentId: `blank-${Date.now()}`,
    title: '未命名文档',
    routeUrl: `/project/${projectId}/documents/blank`,
  };
}

export async function getTemplatePreview(templateId: string): Promise<TemplatePreviewResult> {
  await mockDelay();
  const template = Object.values(mockTemplateStore)
    .flatMap((templates) => templates)
    .find((item) => item.id === templateId);

  return {
    templateId,
    title: template?.title ?? '模板预览',
    previewHtml: '<article><h1>冰箱冷凝器散热效率</h1><p>这里将由后端返回模板预览 HTML、PDF 地址或图片地址。</p></article>',
  };
}

export async function createDocumentFromTemplate(
  projectId: string,
  templateId: string,
): Promise<CreateDocumentResult> {
  await mockDelay();
  return {
    documentId: `template-document-${Date.now()}`,
    title: '由模板创建的文档',
    routeUrl: `/project/${projectId}/documents/from-template/${templateId}`,
  };
}

export async function downloadWorkbenchDocument(documentId: string): Promise<{ downloadUrl: string }> {
  await mockDelay();
  return { downloadUrl: `/mock-api/documents/${documentId}/download` };
}

export async function getSaveAsTemplateBasicInfo(documentId: string): Promise<SaveAsTemplateBasicInfo> {
  await mockDelay();
  return {
    documentId,
    sourceDocumentTitle: '冰箱冷凝器散热效率',
    templateName: '冰箱冷凝器散热效率',
    documentType: 'requirement',
    templateDescription: '沉淀当前文档的章节结构、输入要求与通用检查规则。',
    usageScenario: '新项目中复用标准结构与工作方法',
    documentTypeOptions: [
      { label: '需求文档', value: 'requirement' },
      { label: '评审报告', value: 'review-report' },
      { label: '会议纪要', value: 'meeting-minutes' },
    ],
  };
}

export async function getSaveAsTemplateStructure(documentId: string): Promise<SaveAsTemplateStructureInfo> {
  await mockDelay();
  return {
    documentId,
    chapterCount: 6,
    tableCount: 2,
    placeholderCount: 9,
    items: [
      {
        id: 'structure-1',
        index: 1,
        title: '文档目的与适用范围',
        description: '保留章节结构 · 项目实际值转为占位符',
        checked: true,
      },
      {
        id: 'structure-2',
        index: 2,
        title: '产品背景与用户场景',
        description: '保留章节结构 · 项目实际值转为占位符',
        checked: true,
      },
      {
        id: 'structure-3',
        index: 3,
        title: '功能需求与业务规则',
        description: '包含需求表格 · 保留表头，移除实际数据行',
        checked: true,
      },
      {
        id: 'structure-4',
        index: 4,
        title: '性能与质量指标',
        description: '保留章节结构 · 项目实际值转为占位符',
        checked: true,
      },
      {
        id: 'structure-5',
        index: 5,
        title: '验证方法与验收标准',
        description: '保留章节结构 · 项目实际值转为占位符',
        checked: false,
      },
      {
        id: 'structure-6',
        index: 6,
        title: '交付约束与风险',
        description: '保留章节结构 · 项目实际值转为占位符',
        checked: false,
      },
    ],
  };
}

export async function extractSaveAsTemplate(documentId: string): Promise<SaveAsTemplateExtractResult> {
  await mockDelay();
  const structure = await getSaveAsTemplateStructure(documentId);
  const review = await getSaveAsTemplateReview(documentId);
  const chapterTitles = structure.items.map((i) => i.title);
  const redactionCategories = [...new Set(review.items.map((i) => i.sourceTitle))];
  const summary =
    `已高保真保留 ${structure.chapterCount} 个章节、${structure.tableCount} 个表格；`
    + `实体替换候选 ${review.candidateCount} 项（项目名/企业名/日期等）。`;
  return {
    documentId,
    extractSource: 'llm',
    summary,
    assistantReply: `${summary}\n\n已保留原文档章节与表格结构，并完成实体占位替换。请确认后保存到「我的模版」。`,
    templateName: '需求规格说明模版',
    documentType: 'requirement',
    templateDescription: '由源文档高保真提取的模版（保留章节与表格骨架）',
    usageScenario: '新产品立项与需求评审',
    chapterTitles,
    redactionCategories,
    structure: {
      ...structure,
      items: structure.items.map((item) => ({
        ...item,
        description: item.description?.includes('写作')
          ? `保留本章结构与表格骨架`
          : (item.description || '保留本章原有排版与逻辑结构'),
        contentHtml: item.contentHtml
          || `<h2>${item.title}</h2><table><tr><td>{{ 字段 }}</td><td>{{ 值 }}</td></tr></table>`,
      })),
    },
    review,
  };
}

export async function getSaveAsTemplateReview(documentId: string): Promise<SaveAsTemplateReviewInfo> {
  await mockDelay();
  return {
    documentId,
    candidateCount: 8,
    highRiskCount: 1,
    mediumRiskCount: 4,
    lowRiskCount: 3,
    items: [
      {
        id: 'review-1',
        sourceTitle: '项目与产品标识',
        location: '文档目的与适用范围 / 第 1 段',
        content: '通用款车载空调鉴定项目通用款车载空调鉴定项目通用款车载空调鉴定项目',
        suggestedAction: {
          type: 'placeholder',
          label: '替换为 {{ 项目名称 }}',
          placeholderKey: 'projectName',
          placeholderText: '{{ 项目名称 }}',
          sensitiveType: 'project_name',
        },
        level: 'high',
        action: 'pending',
      },
      {
        id: 'review-2',
        sourceTitle: '人员与联系方式',
        location: '交付约束与风险 / 联系人信息',
        content: '邓大平 / dengdaping@entronova.cn',
        suggestedAction: {
          type: 'placeholder',
          label: '替换为 {{ 项目负责人 }}',
          placeholderKey: 'projectOwner',
          placeholderText: '{{ 项目负责人 }}',
          sensitiveType: 'project_owner',
        },
        level: 'medium',
        action: 'pending',
      },
      {
        id: 'review-3',
        sourceTitle: '文件路径',
        location: '文档路径',
        content: '项目文档 / 产品定义 / V2.3',
        suggestedAction: {
          type: 'placeholder',
          label: '撤销',
          placeholderKey: 'documentPath',
          placeholderText: '{{ 文件路径 }}',
          sensitiveType: 'document_path',
        },
        level: 'low',
        action: 'placeholder',
      },
      {
        id: 'review-4',
        sourceTitle: '项目周期',
        location: '性能与质量指标 / 表格第 3 行',
        content: '2026 年 3 月至 6 月',
        suggestedAction: {
          type: 'placeholder',
          label: '替换为 {{ 项目周期 }}',
          placeholderKey: 'projectPeriod',
          placeholderText: '{{ 项目周期 }}',
          sensitiveType: 'project_period',
        },
        level: 'medium',
        action: 'pending',
      },
      {
        id: 'review-5',
        sourceTitle: '验收地点',
        location: '验证方法与验收标准 / 第 1 条',
        content: '上海临港测试中心',
        suggestedAction: {
          type: 'placeholder',
          label: '替换为 {{ 验收地点 }}',
          placeholderKey: 'acceptanceLocation',
          placeholderText: '{{ 验收地点 }}',
          sensitiveType: 'acceptance_location',
        },
        level: 'medium',
        action: 'pending',
      },
      {
        id: 'review-6',
        sourceTitle: '联系方式',
        location: '交付约束与风险 / 联系人信息',
        content: '021-6888 1200',
        suggestedAction: {
          type: 'placeholder',
          label: '替换为 {{ 联系方式 }}',
          placeholderKey: 'contactInfo',
          placeholderText: '{{ 联系方式 }}',
          sensitiveType: 'contact_info',
        },
        level: 'medium',
        action: 'pending',
      },
      {
        id: 'review-7',
        sourceTitle: '内部流程编号',
        location: '功能需求与业务规则 / 表格第 2 列',
        content: 'REQ-2026-031',
        suggestedAction: {
          type: 'false-positive',
          label: '判定误报',
          sensitiveType: 'internal_process_code',
        },
        level: 'low',
        action: 'pending',
      },
      {
        id: 'review-8',
        sourceTitle: '版本日期',
        location: '文档目的与适用范围 / 页眉',
        content: '2026-08-03',
        suggestedAction: {
          type: 'placeholder',
          label: '替换为 {{ 版本日期 }}',
          placeholderKey: 'versionDate',
          placeholderText: '{{ 版本日期 }}',
          sensitiveType: 'version_date',
        },
        level: 'low',
        action: 'pending',
      },
    ],
  };
}

export async function getSaveAsTemplatePreview(documentId: string): Promise<SaveAsTemplatePreviewInfo> {
  await mockDelay();
  return {
    documentId,
    previewSnapshotId: `save-template-preview-${documentId}-20260803`,
    templateTitle: '冰箱冷凝器制冷效率技术要求书',
    sourceDocumentVersion: 'document-5@2026-09-09T16:24:00+08:00',
    generatedAt: '2026-08-03T16:24:00+08:00',
    defaultSaveScope: 'mine',
    previewHtml: `
      <h2>文档目的与适用范围</h2>
      <p>说明文档目的、读者和使用边界。项目：{{ 项目名称 }}</p>
      <h2>产品背景与用户场景</h2>
      <p>{{ 目标用户与场景 }}</p>
      <table><tr><th>项目</th><th>要求</th></tr><tr><td>效率</td><td>{{ 指标值 }}</td></tr></table>
    `,
    previewSections: [
      {
        id: 'preview-section-1',
        index: 1,
        title: '文档目的与适用范围',
        previewText: '说明文档目的、读者和使用边界',
        structureItemId: 'structure-1',
        bindingStatus: 'static',
      },
      {
        id: 'preview-section-2',
        index: 2,
        title: '产品背景与用户场景',
        previewText: '面向整车热管理项目团队，描述用户场景、使用边界和关键约束',
        structureItemId: 'structure-2',
        placeholderKey: 'targetUserScenario',
        placeholderText: '{{ 目标用户与场景 }}',
        bindingStatus: 'placeholder',
      },
      {
        id: 'preview-section-3',
        index: 3,
        title: '功能需求与业务规则',
        previewText: '沉淀制冷效率、接口适配、运行策略和异常处理等核心需求',
        structureItemId: 'structure-3',
        placeholderKey: 'coreRequirement',
        placeholderText: '{{ 核心需求 }}',
        bindingStatus: 'placeholder',
      },
      {
        id: 'preview-section-4',
        index: 4,
        title: '性能与质量指标',
        previewText: '定义换热效率、稳定性、噪声、功耗和可靠性验证指标',
        structureItemId: 'structure-4',
        placeholderKey: 'performanceMetric',
        placeholderText: '{{ 性能指标 }}',
        bindingStatus: 'placeholder',
      },
      {
        id: 'preview-section-5',
        index: 5,
        title: '验证方法与验收标准',
        previewText: '说明台架测试、整车验证、数据采集方法和验收判定规则',
        structureItemId: 'structure-5',
        placeholderKey: 'verificationMethod',
        placeholderText: '{{ 验证方法 }}',
        bindingStatus: 'placeholder',
      },
      {
        id: 'preview-section-6',
        index: 6,
        title: '交付约束与风险',
        previewText: '记录交付节奏、版本冻结点、跨部门依赖和潜在延期风险',
        structureItemId: 'structure-6',
        placeholderKey: 'deliveryConstraint',
        placeholderText: '{{ 交付约束 }}',
        bindingStatus: 'placeholder',
      },
    ],
    saveScopeOptions: [
      { value: 'mine', label: '我的模板', description: '仅自己可见', enabled: true, permissionCode: 'template:create:mine' },
      { value: 'project', label: '项目模板', description: '需选择具体项目；项目成员可使用，负责人/创建人/模版创建人可删除', enabled: true, permissionCode: 'template:create:project' },
      { value: 'enterprise', label: '企业模板', description: '租户内授权用户可见', enabled: true, permissionCode: 'template:create:enterprise' },
    ],
    skillOptions: [
      {
        id: 'skill-sor-collab-2-1',
        code: 'sor_collaboration_writer',
        name: 'SOR 协作编写',
        version: 'V2.1',
        category: 'document_generation',
        description: '根据占位符和章节结构辅助生成 SOR 草稿',
        checked: true,
      },
      {
        id: 'skill-sor-completeness-1-6',
        code: 'sor_completeness_check',
        name: 'SOR 完整性检查',
        version: 'V1.6',
        category: 'quality_check',
        description: '检查 SOR 章节、字段和约束项是否完整',
        checked: true,
      },
      {
        id: 'skill-sor-risk-1-6',
        code: 'sor_risk_scan',
        name: 'SOR 完整性检查',
        version: 'V1.6',
        category: 'risk_review',
        description: '识别需求表达中的交付风险和模糊约束',
        checked: false,
      },
      {
        id: 'skill-sor-review-1-6',
        code: 'sor_review_assistant',
        name: 'SOR 完整性检查',
        version: 'V1.6',
        category: 'review_assistant',
        description: '辅助评审人生成审阅意见',
        checked: false,
      },
      {
        id: 'skill-sor-output-1-6',
        code: 'sor_output_formatter',
        name: 'SOR 完整性检查',
        version: 'V1.6',
        category: 'formatting',
        description: '统一输出格式、编号和章节样式',
        checked: false,
      },
    ],
  };
}

export async function saveWorkbenchDocumentAsTemplate(
  documentId: string,
  request?: SaveAsTemplateRequest,
): Promise<SaveAsTemplateResult> {
  await mockDelay();
  console.info('[mock document api] save workbench document as template', {
    documentId,
    request,
  });
  const templateId = `template-from-${documentId}-${Date.now()}`;
  const saveScope = request?.saveScope ?? 'mine';
  const templateTitle = request?.templateName ?? '冰箱冷凝器制冷效率技术要求书';
  const previewSectionsForCard = [
    {
      title: '一、标题',
      content: request?.templateDescription || '沉淀当前文档的章节结构、输入要求与通用检查规则。',
      highlighted: true,
    },
    {
      title: '二、标题',
      content: request?.usageScenario || '新项目中复用标准结构与工作方法',
    },
    {
      title: '三、标题',
      content: `已关联 ${request?.selectedSkillIds.length ?? 0} 个 Skill，可用于后续生成和检查。`,
    },
  ];
  mockTemplateStore[saveScope].unshift({
    id: templateId,
    title: templateTitle,
    type: request?.documentType ?? 'requirement',
    version: '1.0.0',
    status: 'published',
    skillCount: request?.selectedSkillIds.length ?? 0,
    scope: saveScope,
    featured: true,
    previewTitle: templateTitle,
    previewSections: previewSectionsForCard,
    updatedAt: '2026-08-03T16:25:00+08:00',
    canManage: true,
    canPublish: false,
    projectId: saveScope === 'project' ? String(request?.projectId || '') : undefined,
  });

  return {
    templateId,
    templateTitle,
    saveScope,
    status: 'published',
    templateVersion: '1.0.0',
    createdAt: '2026-08-03T16:25:00+08:00',
    requiresPublish: false,
  };
}

export async function deleteWorkbenchDocument(documentId: string): Promise<{ success: boolean }> {
  await mockDelay();
  return { success: Boolean(documentId) };
}
